import { defineMessages, type MessageDescriptor } from 'react-intl';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { containsNetworkError } from '@confluence/network';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type { ConvertToLivePageDialogConvertPageToLiveEditMutation_convertPageToLiveEditAction_ConvertPageToLiveEditActionPayload_errors_MutationError } from './__types__/ConvertToLivePageDialogConvertPageToLiveEditMutation.ts';

export const i18n: {
	unknownError: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	serverException: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pageNotFound: {
		id: string;
		// TODO: Fix the direction of the curly quote below. (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: string;
		description: string;
	};
	contentServiceException: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	pageAlreadyLive: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	legacyPageConversionError: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	noEditPermissions: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	draftTitleConflict: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	viewScreenTitleConflict: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	unknownError: {
		id: 'live-pages-features.converttolivepage.modal.error.unknown',
		defaultMessage: 'An error occurred. Please try again.',
		description: 'Dialog content body when error happens in convert to live pages dialog',
	},
	serverException: {
		id: 'live-pages-features.converttolivepage.modal.error.serverException',
		defaultMessage: 'An unexpected error occurred. Please try again later.',
		description:
			'Displayed when an unexpected server-side error prevents the page from being converted to live edit mode.',
	},
	pageNotFound: {
		id: 'live-pages-features.converttolivepage.modal.error.pageNotFound',
		// TODO: Fix the direction of the curly quote below. (see go/curlyquotes)
		/* oxlint-disable-next-line eslint-core/no-restricted-syntax */ // eslint-disable-next-line no-restricted-syntax
		defaultMessage: 'The page you’re trying to edit doesn‘t exist.',
		description:
			'Shown when the user attempts to convert a page to live page, but the specified page ID does not exist or cannot be found.',
	},
	contentServiceException: {
		id: 'live-pages-features.converttolivepage.modal.error.contentServiceException',
		defaultMessage: 'The requested content is not available.',
		description:
			'Appears when the content requested for conversion to a live page cannot be retrieved, possibly due to access restrictions or deletion.',
	},
	pageAlreadyLive: {
		id: 'live-pages-features.converttolivepage.modal.error.pageAlreadyLive.livedocs',
		defaultMessage: 'This page has already been converted to a live doc.',
		description:
			'Updated version of live-pages-features.converttolivepage.modal.error.pageAlreadyLive',
	},
	legacyPageConversionError: {
		id: 'live-pages-features.converttolivepage.modal.error.legacyPageConversionError.livedocs',
		defaultMessage: 'This page can’t be converted to a live doc due to its outdated format.',
		description:
			'Updated version of live-pages-features.converttolivepage.modal.error.legacyPageConversionError',
	},
	noEditPermissions: {
		id: 'live-pages-features.converttolivepage.modal.error.noEditPermissions.livedocs',
		defaultMessage: 'You don’t have the necessary permissions to convert this to a live doc.',
		description:
			'Updated version of live-pages-features.converttolivepage.modal.error.noEditPermissions',
	},
	draftTitleConflict: {
		id: 'live-pages-features.converttolivepage.draft-conversion-title-conflict.description.livedocs',
		defaultMessage:
			'There’s already content named “{pageTitle}” in this space. Give this page a unique title.',
		description:
			'Updated version of live-pages-features.converttolivepage.draft-conversion-title-conflict.description',
	},
	viewScreenTitleConflict: {
		id: 'live-pages-features.converttolivepage.modal.error.view-screen-conversion-title-conflict',
		defaultMessage:
			'There’s already content with this page’s updated title in the space. Give this page a unique title.',
		description:
			'Error message shown when there is other content in the same space with the same title.  This variant is shown when on the view screen when the conflicting draft title is not known.',
	},
});

const BACKEND_ERROR_MESSAGES_BY_KEY: Partial<Record<keyof typeof i18n, string>> = {
	serverException: 'Some server exception',
	pageNotFound: 'Page not found with provided id',
	contentServiceException: 'Content service exception',
	pageAlreadyLive: 'Page subtype is already set to live',
	legacyPageConversionError: 'Legacy page cannot be converted to live edit',
	noEditPermissions: 'Page cannot be converted without edit permissions',
	draftTitleConflict: 'Title already exists',
};
const BACKEND_ERROR_KEYS_BY_MESSAGE: Record<string, Partial<keyof typeof i18n>> = Object.entries(
	BACKEND_ERROR_MESSAGES_BY_KEY,
).reduce((acc, [key, value]) => ({ ...acc, [value]: key }), {});
const HANDLED_BACKEND_ERROR_MESSAGES = [
	BACKEND_ERROR_MESSAGES_BY_KEY.draftTitleConflict,
	BACKEND_ERROR_MESSAGES_BY_KEY.pageAlreadyLive,
	BACKEND_ERROR_MESSAGES_BY_KEY.noEditPermissions,
	BACKEND_ERROR_MESSAGES_BY_KEY.pageNotFound,
];

export const getI18nStringForError = (errorMessage: string): MessageDescriptor => {
	return i18n[BACKEND_ERROR_KEYS_BY_MESSAGE[errorMessage]] || i18n.unknownError;
};

export const submitErrorToMonitoringSystem = (
	error: ApolloErrorInterop | Error,
	contentId: string,
): void => {
	setContentErrorAttributes(error, {
		objectId: contentId,
	});

	getMonitoringClient().submitError(error, {
		attribution: Attribution.PAGE_MODES,
	});
};

export const processError = (
	error:
		| ApolloErrorInterop
		| Error
		| { message: string | null; __typename?: string }
		| ConvertToLivePageDialogConvertPageToLiveEditMutation_convertPageToLiveEditAction_ConvertPageToLiveEditActionPayload_errors_MutationError,
	contentId: string,
): { isHandled: boolean } => {
	let normalizedError: Error | ApolloErrorInterop;
	if (error instanceof Error || error instanceof ApolloErrorInterop) {
		normalizedError = error;
	} else {
		// NOTE: Mutation errors from conversion have a null prototype, causing some below checks to throw an error because `hasOwnProperty` doesn't exist.
		//       Wrapping in a new Error retains original context and ensures the error contains `hasOwnProperty`, so we can handle the errors we're passed without issue.
		normalizedError = new Error(error?.message || 'Unknown error');
		if ('__typename' in error && error?.__typename) normalizedError.name = error?.__typename;
	}
	const isHandledConversionError = HANDLED_BACKEND_ERROR_MESSAGES.includes(normalizedError.message);

	if (
		isUnauthorizedError(normalizedError) ||
		containsNetworkError(normalizedError) ||
		isHandledConversionError
	) {
		markErrorAsHandled(normalizedError);
		return { isHandled: true };
	} else {
		submitErrorToMonitoringSystem(normalizedError, contentId);
		return { isHandled: false };
	}
};
